import { useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type SessionResponse } from '../api.ts';
import { useSession } from '../App.tsx';
import {
  Notice, ErrorNotice, Field, Loading, formatDate, formatAward, TextInput,
} from '../components/Bits.tsx';

type Mode = 'signup' | 'signin' | 'reset';

/**
 * The landing page.
 *
 * Starting an application is the primary action, not signing in. Applicants apply in their
 * final year of school and do not come back in a later cycle, so almost everyone arriving
 * here has no account -- which made "create an account" as a small link under the sign-in
 * form the common case hidden behind the rare one.
 *
 * Signup is still a deliberate choice rather than a side effect of signing in. The Rails
 * version made login double as signup, so a mistyped address silently created a second empty
 * application under a near-miss email; that is where several of the duplicate rows in the
 * legacy data came from.
 */
export function SignIn() {
  const { data, isPending } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<Mode>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);
  // Set on signup so the redirect guard below sends a new applicant to the survey rather
  // than the dashboard. Without it the guard wins the race: refreshing the session makes
  // `data.applicant` truthy, the guard redirects to /apply, and navigate('/welcome') never
  // gets the chance to run.
  const [justSignedUp, setJustSignedUp] = useState(false);

  const afterAuth = async (to: string) => {
    await queryClient.invalidateQueries({ queryKey: ['session'] });
    navigate(to);
  };

  const signIn = useMutation({
    mutationFn: () => api.post<SessionResponse>('/api/session', { email, password }),
    onSuccess: () => afterAuth('/apply'),
  });

  const signUp = useMutation({
    mutationFn: () => api.post<SessionResponse>('/api/applicants', { email, password }),
    // Straight to "how did you hear about us", as the original did. Asked now or not at all.
    onSuccess: () => { setJustSignedUp(true); return afterAuth('/welcome'); },
  });

  const reset = useMutation({
    mutationFn: () => api.post('/api/password-resets', { email }),
    onSuccess: () => setResetSent(true),
  });

  if (isPending) return <main id="main"><Loading /></main>;
  if (data?.applicant) {
    return <Navigate to={justSignedUp ? '/welcome' : '/apply'} replace />;
  }

  const cycle = data?.cycle;
  const open = cycle?.acceptingApplications ?? false;
  const active = mode === 'signin' ? signIn : mode === 'signup' ? signUp : reset;

  const submitLabel = active.isPending
    ? 'Working…'
    : mode === 'signup' ? 'Create account'
    : mode === 'signin' ? 'Sign in'
    : 'Send a temporary password';

  return (
    <main id="main">
      <div className="landing">
        <section>
          <h2 style={{ marginTop: 0 }}>The {cycle?.year ?? ''} SEED Foundation Scholarship</h2>
          <p className="lede">
            {cycle
              ? `A ${formatAward(cycle.awardAmount, cycle.awardYears)}, for graduating high school seniors of Asian-Indian heritage entering a U.S. university.`
              : 'For graduating high school seniors of Asian-Indian heritage entering a U.S. university.'}
          </p>

          {cycle && (
            <Notice kind={open ? 'ok' : 'info'}>
              {open
                ? <>Applications are open until <strong>{formatDate(cycle.closesAt)}</strong>.</>
                : cycle.reason === 'not-yet-open'
                  ? <>Applications open on <strong>{formatDate(cycle.opensAt)}</strong>.</>
                  : <>Applications for {cycle.year} are closed. Winners are announced in the summer.</>}
            </Notice>
          )}

          <div className="panel">
            <h3 style={{ marginTop: 0, fontSize: '0.95rem' }}>
              You must meet all of these to be considered
            </h3>
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              <li>Of Asian-Indian heritage, with at least one parent of Indian ancestry</li>
              <li>A graduating U.S. high school senior{cycle ? ` (Class of ${cycle.year})` : ''}</li>
              <li>A minimum GPA of 3.0 on a 4.0 scale</li>
              <li>
                Enrolling full-time as an undergraduate at a U.S. university
                {cycle ? ` in the Fall of ${cycle.year}` : ''}
              </li>
              <li>A legal resident of the United States</li>
              <li>The award covers tuition only — not room, board or other expenses</li>
            </ul>
            <p className="hint" style={{ marginBottom: 0, marginTop: '0.7rem' }}>
              Read the full <Link to="/guidelines">guidelines</Link> and the{' '}
              <Link to="/faq">frequently asked questions</Link> before you start.
            </p>
          </div>
        </section>

        <section>
          <div className="panel">
            <div className="choice">
              <button
                className={mode === 'signup' ? '' : 'secondary'}
                aria-pressed={mode === 'signup'}
                onClick={() => { setMode('signup'); setResetSent(false); }}
              >
                Start a new application
              </button>
              <button
                className={mode === 'signup' ? 'secondary' : ''}
                aria-pressed={mode !== 'signup'}
                onClick={() => { setMode('signin'); setResetSent(false); }}
              >
                Already have an account? Sign in
              </button>
            </div>

            {mode === 'signup' && !open && cycle && (
              <Notice kind="info">
                New applications are not being accepted right now. If you have already started
                one, you can still sign in to see it.
              </Notice>
            )}

            {resetSent && (
              <Notice kind="ok">
                If that email address has an account, a temporary password is on its way. Check
                your spam folder if it does not arrive shortly.
              </Notice>
            )}

            <ErrorNotice error={active.error} />

            <form
              onSubmit={(event) => {
                event.preventDefault();
                setResetSent(false);
                active.mutate();
              }}
            >
              {mode === 'reset' && (
                <p className="hint" style={{ marginTop: 0 }}>
                  Enter the address you applied with and we will send a temporary password.
                </p>
              )}

              <Field
                label="Email address"
                hint={mode === 'signup'
                  ? 'Use an address you will still have access to next summer since we will use this to communicate with you.'
                  : undefined}
              >
                <TextInput
                  type="email" value={email} autoComplete="email" required
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>

              {mode !== 'reset' && (
                <Field
                  label="Password"
                  hint={mode === 'signup' ? 'At least 8 characters.' : undefined}
                >
                  <TextInput
                    type="password" value={password} required
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    minLength={mode === 'signup' ? 8 : undefined}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>
              )}

              <div className="actions">
                <button type="submit" disabled={active.isPending}>{submitLabel}</button>
                {mode === 'reset' && (
                  <button type="button" className="secondary" onClick={() => setMode('signin')}>
                    Back
                  </button>
                )}
              </div>
            </form>

            {mode === 'signin' && (
              <p className="hint" style={{ marginBottom: 0 }}>
                <button className="quiet" style={{ paddingLeft: 0 }} onClick={() => setMode('reset')}>
                  Forgotten your password?
                </button>
              </p>
            )}

            {signUp.error instanceof ApiError && signUp.error.status === 409 && (
              <Notice kind="info">
                <button className="quiet" onClick={() => setMode('signin')}>Sign in instead</button>
                {' or '}
                <button className="quiet" onClick={() => setMode('reset')}>reset your password</button>.
              </Notice>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
